import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, FileText, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { getPaymentStatus, exportFilePaid } from "@/lib/api";

const PENDING_KEY = "riclassificatore.pending_export.v1";
const PAID_KEY = "riclassificatore.paid_session.v1";

const MAX_POLL = 30;   // total attempts
const POLL_MS = 2000;  // interval

export default function PaymentSuccess() {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");   // polling | paid | failed | error
  const [downloadedKind, setDownloadedKind] = useState(null);
  const [amount, setAmount] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);

  const t = (it, en) => (lang === "it" ? it : en);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let poll;

    const tryDownload = async () => {
      let staged;
      try { staged = JSON.parse(localStorage.getItem(PENDING_KEY) || "null"); } catch { staged = null; }
      if (!staged?.payload) return;
      const kind = staged.kind || "pdf";
      const p = staged.payload;
      try {
        await exportFilePaid(kind, sessionId, p.reclassified, p.ratios, p.lang || lang, p.cover, p.advisorText, p.correctives, p.roadmap);
        setDownloadedKind(kind);
        localStorage.removeItem(PENDING_KEY);
      } catch (e) {
        toast.error(t("Impossibile scaricare il PDF automaticamente. Torna ai risultati e clicca Export.",
                      "Auto-download failed. Return to results and click Export."));
      }
    };

    const poller = async () => {
      if (cancelled) return;
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.amount_total) setAmount(s.amount_total);
        if (s.payment_status === "paid" || s.status === "complete") {
          setStatus("paid");
          localStorage.setItem(PAID_KEY, sessionId);
          await tryDownload();
          return;
        }
        if (s.payment_status === "failed" || s.status === "expired") {
          setStatus("failed");
          return;
        }
        attemptsRef.current += 1;
        setAttempts(attemptsRef.current);
        if (attemptsRef.current >= MAX_POLL) {
          setStatus("error");
          return;
        }
        poll = setTimeout(poller, POLL_MS);
      } catch (e) {
        setStatus("error");
      }
    };

    poller();

    return () => { cancelled = true; if (poll) clearTimeout(poll); };
  }, [sessionId, lang]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="mx-auto max-w-2xl px-6 py-16" data-testid="payment-success-page">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          {status === "polling" && (
            <div className="text-center">
              <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" strokeWidth={1.5} />
              <h1 className="font-heading text-2xl font-semibold text-slate-900">
                {t("Verifica del pagamento in corso...", "Verifying payment...")}
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                {t("Attendere qualche istante mentre Stripe conferma la transazione.", "Please wait while Stripe confirms the transaction.")}
              </p>
              <div className="mt-4 text-xs text-slate-400">{t("Tentativo", "Attempt")} {attempts + 1}/{MAX_POLL}</div>
            </div>
          )}

          {status === "paid" && (
            <div className="text-center" data-testid="payment-success-paid">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-green-100 text-green-700">
                <CheckCircle2 className="h-8 w-8" strokeWidth={2} />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">
                {t("Pagamento confermato — Grazie!", "Payment confirmed — Thank you!")}
              </h1>
              {amount != null && (
                <p className="mt-1 text-sm text-slate-500">
                  {t("Importo addebitato", "Amount charged")}: <b>€{(amount/100).toFixed(2)}</b>
                </p>
              )}
              <p className="mt-4 text-sm text-slate-700">
                {downloadedKind
                  ? t(`Il tuo report ${downloadedKind.toUpperCase()} è stato scaricato automaticamente.`, `Your ${downloadedKind.toUpperCase()} report was downloaded automatically.`)
                  : t("Ora puoi tornare ai risultati e scaricare il report senza limitazioni.", "You can now return to the results and download the report without restrictions.")}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/riclassifica">
                  <Button data-testid="back-to-results-btn" className="rounded-full bg-blue-700 text-white hover:bg-blue-800">
                    <FileText className="mr-2 h-4 w-4" strokeWidth={2} />
                    {t("Torna al report", "Back to report")}
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
                  </Button>
                </Link>
              </div>
              <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-xs text-slate-600">
                <div className="mb-1 font-semibold text-slate-800">
                  {t("Sessione di pagamento", "Payment session")}
                </div>
                <div className="font-mono break-all text-slate-500">{sessionId}</div>
              </div>
            </div>
          )}

          {(status === "failed" || status === "error") && (
            <div className="text-center" data-testid="payment-error">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-red-100 text-red-700">
                <XCircle className="h-8 w-8" strokeWidth={2} />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">
                {t("Pagamento non riuscito", "Payment failed")}
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                {t("Riprova la transazione oppure contatta Finch & Partners.", "Please retry the transaction or contact Finch & Partners.")}
              </p>
              <div className="mt-6">
                <Link to="/riclassifica">
                  <Button variant="outline" className="rounded-full border-slate-300">
                    {t("Torna al report", "Back to report")}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
